import React, { useState } from 'react';
import { Modal, Upload, message, Checkbox, List } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { COLORS } from '../../../constants/colors';
import OrdersService from '../../../services/orders/service';

const { Dragger } = Upload;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
`;

const OrderDetails = styled.div`
  margin-bottom: 24px;
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 500;
`;

const CustomerName = styled.p`
  font-size: 16px;
  color: ${COLORS.$gray600};
  margin: 0;
`;

const UploadContainer = styled.div`
  border-radius: 8px;
  text-align: center;
  background: ${COLORS.$gray50};
  margin-bottom: 16px;
  
  &:hover {
    border-color: ${COLORS.$primary};
  }
`;

const UploadedFileList = styled.div`
  margin-top: 16px;
`;

const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${COLORS.$gray200};
  border-radius: 4px;
  margin-bottom: 8px;
`;

const FileName = styled.span`
  font-size: 14px;
  color: ${COLORS.$gray700};
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.$gray500};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${COLORS.$red500};
  }
`;

const FulfillButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${COLORS.$primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 16px;
  
  &:hover {
    background-color: ${COLORS.$primaryDark};
  }
  
  &:disabled {
    background-color: ${COLORS.$greyScale500};
    cursor: not-allowed;
  }
`;

const CustomerRequest = styled.div`
  margin-top: 16px;
  background: ${COLORS.$gray50};
  border-radius: 8px;
  padding: 16px;
`;

const RequestLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.$gray600};
  margin-bottom: 8px;
`;

const RequestText = styled.div`
  font-size: 16px;
  color: ${COLORS.$gray800};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const EngagementSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: ${COLORS.$gray50};
  border-radius: 8px;
`;

const EngagementList = styled(List)`
  .ant-list-item {
    padding: 12px;
    background: white;
    border-radius: 4px;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const EngagementItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const EngagementTitle = styled.span`
  font-size: 15px;
  color: ${COLORS.$gray800};
`;

const EngagementDetail = styled.span`
  font-size: 14px;
  color: ${COLORS.$gray600};
  margin-left: 8px;
`;

const OrderFulfillmentModal = ({ visible, order, onCancel, updateOrderStatus }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completedTasks, setCompletedTasks] = useState({});

  const isEngagementOrder = order?.orderType === 'engagement';

  const uploadProps = {
    name: 'file',
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      setFileList(prev => [...prev, file]);
      return false;
    },
    onRemove: (file) => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
    }
  };

  const handleCustomOrderFulfill = async () => {
    if (!fileList.length) {
      message.error('Please upload at least one file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      fileList.forEach(file => {
        formData.append('files', file);
      });

      const uploadResponse = await OrdersService.uploadFulfillment(order._id, formData);

      if (!uploadResponse.success) {
        throw new Error('File upload failed');
      }

      await updateOrderStatus(order._id, {
        status: 'fulfilled',
        notes: `Files uploaded: ${fileList.map(f => f.name).join(', ')}`
      });

      message.success('Order fulfilled successfully');
      onCancel();
    } catch (error) {
      message.error('Failed to fulfill order');
      console.error('Fulfillment error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleEngagementOrderFulfill = async () => {
    const activeFeatures = Object.entries(order.features)
      .filter(([, value]) => value)
      .map(([key]) => key);

    const allTasksCompleted = activeFeatures.every(feature => completedTasks[feature]);

    if (!allTasksCompleted) {
      message.error('Please complete all engagement tasks');
      return;
    }

    try {
      await updateOrderStatus(order._id, {
        status: 'completed',
      });

      message.success('Engagement order fulfilled successfully');
      onCancel();
    } catch (error) {
      message.error('Failed to fulfill engagement order');
      console.error('Engagement fulfillment error:', error);
    }
  };

  const toggleTaskCompletion = (taskKey) => {
    setCompletedTasks(prev => ({
      ...prev,
      [taskKey]: !prev[taskKey]
    }));
  };

  const renderEngagementTasks = () => {
    const tasks = [];
    
    if (order.features.follows) {
      tasks.push({
        key: 'follows',
        title: 'Follows',
        detail: `Follow ${order.followUsername} on ${order.followPlatform}`
      });
    }

    if (order.features.likes) {
      tasks.push({
        key: 'likes',
        title: 'Likes',
        detail: `Like post: ${order.likesPostLink}`
      });
    }

    if (order.features.comments) {
      tasks.push({
        key: 'comments',
        title: 'Comments',
        detail: `Comment on: ${order.commentsPostLink}`
      });
    }

    if (order.features.shoutouts) {
      tasks.push({
        key: 'shoutouts',
        title: 'Shoutouts',
        detail: `Create ${order.shoutoutType} shoutout`
      });
    }

    if (order.features.custom) {
      tasks.push({
        key: 'custom',
        title: 'Custom Request',
        detail: order.customDetails
      });
    }

    return (
      <EngagementList
        dataSource={tasks}
        renderItem={item => (
          <List.Item>
            <EngagementItem>
              <div>
                <EngagementTitle>{item.title}</EngagementTitle>
                <EngagementDetail>{item.detail}</EngagementDetail>
              </div>
              <Checkbox
                checked={completedTasks[item.key]}
                onChange={() => toggleTaskCompletion(item.key)}
              />
            </EngagementItem>
          </List.Item>
        )}
      />
    );
  };

  return (
    <Modal
      visible={visible}
      title={<ModalTitle>Fulfill Your Order</ModalTitle>}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <OrderDetails>
        <ProductName>
          {isEngagementOrder ? 'Engagement Services' : order?.productName}
        </ProductName>
        <CustomerName>{order?.customerName}</CustomerName>
        {order?.customerNotes && (
          <CustomerRequest>
            <RequestLabel>Customer Request:</RequestLabel>
            <RequestText>{order.customerNotes}</RequestText>
          </CustomerRequest>
        )}
      </OrderDetails>

      {isEngagementOrder ? (
        <EngagementSection>
          <RequestLabel>Complete the following tasks:</RequestLabel>
          {renderEngagementTasks()}
          <FulfillButton
            onClick={handleEngagementOrderFulfill}
            disabled={uploading}
          >
            Mark as Fulfilled
          </FulfillButton>
        </EngagementSection>
      ) : (
        <>
          <UploadContainer>
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag files to upload
              </p>
              <p className="ant-upload-hint">
                If you are uploading multiple files for this order, please combine them into a single .zip file and then upload it!
              </p>
            </Dragger>
          </UploadContainer>

          {fileList.length > 0 && (
            <UploadedFileList>
              {fileList.map(file => (
                <FileItem key={file.uid}>
                  <FileName>{file.name}</FileName>
                  <DeleteButton onClick={() => uploadProps.onRemove(file)}>
                    <DeleteOutlined />
                  </DeleteButton>
                </FileItem>
              ))}
            </UploadedFileList>
          )}

          <FulfillButton
            onClick={handleCustomOrderFulfill}
            disabled={uploading || !fileList.length}
          >
            {uploading ? 'Uploading...' : 'Fulfill Order'}
          </FulfillButton>
        </>
      )}
    </Modal>
  );
};

export default OrderFulfillmentModal;